import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type RoleplaySessionResponse, fetchRoleplaySession } from './roleplaySession.ts';
import { reportError } from './reportError.ts';
import {
  type RoleplaySessionFailure,
  roleplaySessionFailure,
} from './roleplaySessionGuard.ts';

/** One practice conversation, as either of the two screens that host it reads it. */
export interface RoleplaySessionQuery {
  /** Undefined until the conversation is there, and whenever it cannot be. */
  session: RoleplaySessionResponse | undefined;
  isPending: boolean;
  /** Null while nothing has gone wrong. */
  failure: RoleplaySessionFailure | null;
}

/**
 * The conversation behind a session id, for whoever is showing it.
 *
 * The same request from two places — the practice modal on Oefenen and the
 * dedicated address a chat link opens — so both wait on the same cache entry
 * and name a failure the same way. Only `unreachable` is reported: a link that
 * ran out or a conversation that is not theirs is an answer, not a fault, and
 * reporting those buries the one failure that is ours. Waiting on the
 * feedback is the conversation's own business, and it asks again itself.
 */
export function useRoleplaySession(sessionId: string): RoleplaySessionQuery {
  const {
    data: session,
    isPending,
    error,
  } = useQuery({
    queryKey: ['roleplaySession', sessionId],
    queryFn: () => fetchRoleplaySession(sessionId),
  });

  const failure = error === null ? null : roleplaySessionFailure(error);

  useEffect(() => {
    if (failure === 'unreachable') {
      reportError(error, {
        component: 'useRoleplaySession',
        action: 'load',
        session_id: sessionId,
      });
    }
  }, [failure, error, sessionId]);

  return { session, isPending, failure };
}
